import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { OrganizationProfile } from '@/lib/api/org-profile';
import type { PublicDirectorProfile } from '@swiss-magic/shared-types';

// Public-page host attribution. White-label tenants with an
// organization profile show the org as the host (with the director
// as the operator below). Public-offering tenants fall through to
// the legacy "Organized by <director>" block. Either way, when the
// viewer can subscribe to the director, the subscribe/unsubscribe
// affordance hangs off the bottom of the block.
//
// Extracted from TournamentPublicPage to keep that page focused on
// orchestration.

interface Props {
  orgProfile: OrganizationProfile | null;
  directorProfile: PublicDirectorProfile | null;
  subscribable: boolean;
  showSubscribeButton: boolean;
  showUnsubscribeButton: boolean;
  submittingSubscription: boolean;
  onSubscribe: () => void;
  onUnsubscribe: () => void;
}

export function TournamentHostBlock({
  orgProfile,
  directorProfile,
  subscribable,
  showSubscribeButton,
  showUnsubscribeButton,
  submittingSubscription,
  onSubscribe,
  onUnsubscribe,
}: Props) {
  const { t } = useTranslation('tournament');

  if (orgProfile?.name) {
    // White-label: org-as-host (story 064). Director shows below as
    // the operator. No subscribe affordance in this block — that lives
    // on the org-attribution branch.
    return (
      <div data-testid="hosted-by-block">
        <p className="text-sm font-medium text-muted-foreground">
          {t('public_page.host.hosted_by')}
        </p>
        <div className="mt-2 flex flex-col gap-2">
          {orgProfile.logoUrl ? (
            <img
              data-testid="org-logo"
              src={orgProfile.logoUrl}
              alt={orgProfile.name}
              className="max-h-24 max-w-sm rounded border bg-muted/30 object-contain"
            />
          ) : null}
          <div>
            <p data-testid="tournament-host" className="text-sm font-medium">
              {orgProfile.name}
            </p>
            {orgProfile.description && (
              <p className="text-xs text-muted-foreground">
                {orgProfile.description}
              </p>
            )}
          </div>
          {directorProfile && (
            <p
              data-testid="tournament-director"
              className="text-xs text-muted-foreground"
            >
              {t('public_page.host.director_prefix', { name: directorProfile.name })}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!directorProfile && !subscribable) return null;

  return (
    <div data-testid="organized-by-block">
      <p className="text-sm font-medium text-muted-foreground">
        {t('public_page.host.organized_by')}
      </p>
      <div className="mt-2 flex flex-col gap-2">
        {directorProfile?.logoUrl ? (
          <img
            data-testid="organizer-logo"
            src={directorProfile.logoUrl}
            alt={directorProfile.name}
            className="max-h-24 max-w-sm rounded border bg-muted/30 object-contain"
          />
        ) : null}
        {directorProfile && (
          <div>
            <p data-testid="organizer-name" className="text-sm font-medium">
              {directorProfile.name}
            </p>
            {directorProfile.description && (
              <p
                data-testid="organizer-description"
                className="text-xs text-muted-foreground"
              >
                {directorProfile.description}
              </p>
            )}
          </div>
        )}
        {(showSubscribeButton || showUnsubscribeButton) && (
          <div className="flex flex-wrap items-center gap-2">
            {showSubscribeButton && (
              <Button
                size="sm"
                variant="outline"
                data-testid="public-subscribe-button"
                onClick={onSubscribe}
                disabled={submittingSubscription}
                className="self-start"
              >
                {submittingSubscription
                  ? t('public_page.buttons.subscribing')
                  : t('public_page.buttons.subscribe')}
              </Button>
            )}
            {showUnsubscribeButton && (
              <>
                <Badge
                  data-testid="public-subscribed-badge"
                  variant="secondary"
                  className="self-start text-sm py-1 px-3"
                >
                  {t('public_page.badges.subscribed')}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="public-unsubscribe-button"
                  onClick={onUnsubscribe}
                  disabled={submittingSubscription}
                  className="self-start"
                >
                  {submittingSubscription
                    ? t('public_page.buttons.unsubscribing')
                    : t('public_page.buttons.unsubscribe')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
